import { Card, Container, Header, Heading, Icon } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import parse from 'html-react-parser'
import { ComplaintFlowInputs, ComplaintFlowProps } from './types'
import { FormProvider, useForm } from 'react-hook-form'
import Contact from 'features/complaint/views/flow/steps/Contact'
import Complaint from 'features/complaint/views/flow/steps/Complaint'
import Overview from 'features/complaint/views/flow/steps/Overview'
import styles from './ComplaintFlow.module.scss'
import { getStepState } from 'features/complaint/views/flow/utils'

const ActiveComplaintStep = (props: ComplaintFlowProps) => {
  switch (props.currentStep) {
    case 'complaint': {
      return <Complaint {...props} />
    }

    case 'overview': {
      return <Overview {...props} />
    }

    default: {
      return <Contact {...props} />
    }
  }
}

const ComplaintFlow = (props: ComplaintFlowProps) => {
  // i18n
  const { t } = useTranslation('complaint', { keyPrefix: 'flow' })

  // React Hook Form
  const hookForm = useForm<ComplaintFlowInputs>({
    mode: 'onChange',
    defaultValues: {
      contact: {
        firstName: '',
        lastName: '',
        email: '',
        mobilePhone: ''
      },
      complaint: {
        subject: '',
        description: '',
        attachments: null
      },
      privacyAgreement: false
    }
  })

  return (
    <main>
      <Header
        title={t('header.title')}
        color="accent-soft"
        sideContent={
          <Card className="gap-200 mt-auto">
            <Heading as="h2" variant="h5">
              {t('header.subtitle')}
            </Heading>
            <p>{parse(t('header.contact'))}</p>
          </Card>
        }
      >
        <p>{t('header.description')}</p>
      </Header>

      <Container as="section" inset color="light" classnames={{ content: styles.flow }}>
        <Card className={styles.indicator}>
          <ol>
            <li>
              <small data-state={getStepState('contact', props.currentStep)}>
                {props.currentStep !== 'contact' ? <Icon name="check" /> : 1}
              </small>{' '}
              <span>{t('steps.contact.short')}</span>
            </li>
            <li>
              <small data-state={getStepState('complaint', props.currentStep)}>
                {props.currentStep === 'overview' ? <Icon name="check" /> : 2}
              </small>
              <span>{t('steps.complaint.short')}</span>
            </li>
            <li>
              <small data-state={getStepState('overview', props.currentStep)}>3</small>
              <span>{t('steps.overview.short')}</span>
            </li>
          </ol>
        </Card>

        <FormProvider {...hookForm}>
          <ActiveComplaintStep {...props} />
        </FormProvider>
      </Container>
    </main>
  )
}

export default ComplaintFlow
